/**
 * Validity of an external user's personal guest link: "Permanent länk" or
 * "Giltig till [datum]". The composer pre-selects Giltig till 90 days from
 * today (spec: external-user-links, "Validity"); the same default is used by
 * the functions when the web sends nothing.
 */
import { LinkValidity } from '../api/externalLinksApi';
import { ExternalLinkTask, GuestLinkLogEntry, GuestLinkSummary, GuestLinkValidityPayload } from '../types/guestAccess';
import { addInterval, formatDateForDisplay, formatDateOnlyForStorage } from '../Helpers/DateUtils';

export const DEFAULT_LINK_PERIOD_DAYS = 90;

/** A date usable for date math (`YYYY-MM-DD`, optionally with a time). */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;

/** Expiring, `DEFAULT_LINK_PERIOD_DAYS` from `now` (Stockholm calendar date). */
export function defaultLinkValidity(now: Date = new Date()): LinkValidity {
    return {
        kind: 'expiring',
        expiresAt: addInterval(formatDateOnlyForStorage(now), { value: DEFAULT_LINK_PERIOD_DAYS, unit: 'days' }),
    };
}

/**
 * True while the link grants access. An expired link is never revived: an
 * admin action (a delegation, send or "Skapa ny länk") replaces it with a new
 * one, so it counts as no link here.
 */
export function isActiveLink(link: GuestLinkSummary | null | undefined): link is GuestLinkSummary {
    return !!link && link.state === 'active';
}

/** The URL to offer for copying: only an active link's (an expired one leads to a parked page). */
export function copyableLinkUrl(link: GuestLinkSummary | null | undefined): string | undefined {
    return isActiveLink(link) ? link.url : undefined;
}

/**
 * The validity fields sent with a delegation: the chosen (or pre-filled)
 * validity, nothing when there is none (an active link left alone; for a new
 * link the host keeps the submit disabled until one is chosen).
 */
export function linkValidityPayload(validity: LinkValidity | null): GuestLinkValidityPayload {
    if (!validity) return {};
    if (validity.kind === 'permanent') return { linkKind: 'permanent' };
    // An empty date (cleared input) is never sent; the submit is disabled by isValidityComplete.
    return validity.expiresAt ? { linkKind: 'expiring', expiresAt: validity.expiresAt } : { linkKind: 'expiring' };
}

/**
 * False while "Giltig till" is chosen without a usable date (empty, malformed
 * or before today in Stockholm); gates every submit that sends a validity.
 */
export function isValidityComplete(
    validity: LinkValidity | null,
    today: string = formatDateOnlyForStorage(new Date())
): boolean {
    if (!validity || validity.kind === 'permanent') return true;
    return !!validity.expiresAt && ISO_DATE.test(validity.expiresAt) && validity.expiresAt >= today;
}

/** The validity a link currently has, as a control value (an expired link suggests the default period). */
export function validityOf(link: GuestLinkSummary): LinkValidity {
    if (link.kind === 'permanent') return { kind: 'permanent' };
    return link.state === 'expired' || !link.expiresAt ? defaultLinkValidity() : { kind: 'expiring', expiresAt: link.expiresAt };
}

/** Days from today (Stockholm) to a YYYY-MM-DD date; negative when past. */
export function daysUntil(date: string, today: string = formatDateOnlyForStorage(new Date())): number {
    return Math.round((Date.parse(date.slice(0, 10)) - Date.parse(today)) / 86_400_000);
}

/** Within this many days an active link shows "går ut om N dagar". */
export const EXPIRY_WARNING_DAYS = 30;

/** "går ut idag", "går ut imorgon", "går ut om 12 dagar"; undefined unless an active expiring link is within the warning window. */
export function expiryWarning(link: GuestLinkSummary | null | undefined, today?: string): string | undefined {
    if (!isActiveLink(link) || link.kind !== 'expiring' || !link.expiresAt) return undefined;
    return expiryWarningFor(link.expiresAt, today);
}

/** The same warning for a bare end date (the guest portal has no link summary); undefined beyond the window. */
export function expiryWarningFor(expiresAt: string, today?: string): string | undefined {
    const days = daysUntil(expiresAt, today);
    if (days > EXPIRY_WARNING_DAYS) return undefined;
    return days <= 0 ? 'går ut idag' : days === 1 ? 'går ut imorgon' : `går ut om ${days} dagar`;
}

/** Short state text for a link: "Permanent", "Aktiv till 2027-01-10 (går ut om 12 dagar)", "Utgången", "Återkallad" or "Ingen länk". */
export function describeLinkState(link: GuestLinkSummary | null | undefined): string {
    if (!link) return 'Ingen länk';
    if (link.state === 'revoked') return 'Återkallad';
    if (link.state === 'expired') return 'Utgången';
    if (link.kind === 'permanent') return 'Permanent';
    const warning = expiryWarning(link);
    return `Aktiv till ${formatDateForDisplay(link.expiresAt)}${warning ? ` (${warning})` : ''}`;
}

/** Days added after the latest due date when suggesting a link's end date. */
export const SUGGESTION_MARGIN_DAYS = 14;

/** A suggested end date and the task that drives it. */
export interface ValiditySuggestion {
    expiresAt: string;
    basis: { title: string; dueDate: string };
}

/**
 * Suggest an end date for a link: the latest due date among the user's open
 * tasks and the task being delegated, plus SUGGESTION_MARGIN_DAYS (counted
 * from today when that date has passed). Undated tasks are ignored; null
 * when nothing has a date — the admin must choose.
 */
export function suggestLinkValidity(
    tasks: ExternalLinkTask[],
    current?: { title: string; dueDate?: string | null },
    today: string = formatDateOnlyForStorage(new Date())
): ValiditySuggestion | null {
    const dated = [
        ...tasks.filter((t) => !t.completedAt && t.dueDate && ISO_DATE.test(t.dueDate)).map((t) => ({ title: t.title, dueDate: (t.dueDate as string).slice(0, 10) })),
        ...(current?.dueDate && ISO_DATE.test(current.dueDate) ? [{ title: current.title, dueDate: current.dueDate.slice(0, 10) }] : []),
    ];
    if (dated.length === 0) return null;
    // Malformed dates (legacy `2026/11/01`) are skipped above; addInterval would throw during render.
    const basis = dated.reduce((latest, t) => (t.dueDate > latest.dueDate ? t : latest));
    const from = basis.dueDate > today ? basis.dueDate : today;
    return { expiresAt: addInterval(from, { value: SUGGESTION_MARGIN_DAYS, unit: 'days' }), basis };
}

/** When the user's newest revocation happened (YYYY-MM-DD hh:mm:ss), from the merged log; undefined if never revoked. */
export function lastRevokedAt(log: GuestLinkLogEntry[]): string | undefined {
    return log.filter((e) => e.type === 'revoked').map((e) => e.when).sort().pop();
}

/** Swedish genitive of a name: "Eriks", but "Lars", "Max", "Inez" as they are. */
export function swedishGenitive(name: string): string {
    return /[sxz]$/i.test(name) ? name : `${name}s`;
}
